import {
  Badge,
  Card,
  CardContent,
  cn,
  ghostAccentIconTileClass,
  healthAttentionIconTileClass,
  healthAttentionSurfaceClass,
  healthCriticalIconTileClass,
  healthCriticalSurfaceClass,
} from '@up2stay/up2promo-ui';
import { AlertTriangle, Clock, Loader2, Sparkles } from 'lucide-react';

import { useReputationSync } from '@/core/components/reputation/reputation-sync-context';
import {
  REPUTATION_SECTION_MUTED_CLASS,
  REPUTATION_SECTION_TITLE_CLASS,
} from '@/core/components/reputation/reputation-surface';
import { useLocale } from '@/core/hooks/use-locale';
import { useDictionary } from '@/core/providers/dictionary-provider';

function formatScheduledTime(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function ReputationSyncBanner({ className }: { className?: string }) {
  const { banner } = useReputationSync();
  const dict = useDictionary();
  const syncDict = dict.reputation?.sync;
  const locale = useLocale();

  if (!banner.visible || !banner.variant || !syncDict || !banner.category) {
    return null;
  }

  const copy = syncDict[banner.variant];
  const isError = banner.variant === 'failed';
  const isGoogle = banner.category === 'google';
  const isScheduled = banner.variant === 'reanalysisScheduled';
  const isActiveInsights = banner.variant === 'reanalyzing';
  const isActiveGoogle =
    banner.variant === 'syncing' ||
    banner.variant === 'refreshing' ||
    banner.variant === 'pending';

  const categoryLabel = isGoogle
    ? syncDict.banner?.googleLabel
    : syncDict.banner?.insightsLabel;

  const statusBadge = isError
    ? syncDict.banner?.statusFailed
    : isScheduled
      ? syncDict.banner?.statusScheduled
      : isActiveInsights
        ? syncDict.banner?.statusUpdating
        : isActiveGoogle
          ? syncDict.banner?.statusSyncing
          : syncDict.banner?.statusPending;

  const description =
    isScheduled && banner.scheduledReanalysisAt
      ? (
          syncDict.reanalysisScheduled?.descriptionWithTime ?? copy.description
        ).replace(
          '{time}',
          formatScheduledTime(banner.scheduledReanalysisAt, locale),
        )
      : isActiveInsights && banner.pendingAnalysisCount > 0
        ? (
            syncDict.reanalyzing?.descriptionWithCount ?? copy.description
          ).replace('{count}', String(banner.pendingAnalysisCount))
        : copy.description;

  const surfaceClass = isError
    ? healthCriticalSurfaceClass
    : isScheduled
      ? healthAttentionSurfaceClass
      : undefined;

  const iconWrapClass = isError
    ? healthCriticalIconTileClass
    : isScheduled
      ? healthAttentionIconTileClass
      : ghostAccentIconTileClass;

  return (
    <Card
      className={cn('overflow-hidden shadow-none', surfaceClass, className)}
    >
      <CardContent className="flex items-start gap-3 p-4 sm:p-5">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-control)]',
            iconWrapClass,
          )}
        >
          {isError ? (
            <AlertTriangle className="h-4 w-4" aria-hidden />
          ) : isScheduled ? (
            <Clock className="h-4 w-4" aria-hidden />
          ) : isActiveGoogle || isActiveInsights ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : isGoogle ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="h-4 w-4" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {categoryLabel ? (
              <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {categoryLabel}
              </span>
            ) : null}
            {statusBadge ? (
              <Badge
                variant={
                  isError
                    ? 'destructive'
                    : isScheduled
                      ? 'warning'
                      : 'secondary'
                }
                className="font-normal"
              >
                {statusBadge}
              </Badge>
            ) : null}
          </div>

          <p className={REPUTATION_SECTION_TITLE_CLASS}>{copy.title}</p>

          <p className={REPUTATION_SECTION_MUTED_CLASS}>{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}
